import React, { createContext, useContext, useEffect, useMemo, useRef, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { settingsService } from '../services/settingsService';
import { Settings, SettingsUpdate } from '../types/settings';
import { toast } from 'react-hot-toast';

interface SettingsContextType {
  settings: Settings | undefined;
  isLoading: boolean;
  error: Error | null;
  updateSettings: (updates: SettingsUpdate) => Promise<void>;
  updateProfile: (updates: Partial<Settings['profile']>) => Promise<void>;
  updateNotifications: (updates: Partial<Settings['notifications']>) => Promise<void>;
  updatePrivacy: (updates: Partial<Settings['privacy']>) => Promise<void>;
  updateSecurity: (updates: Partial<Settings['security']>) => Promise<void>;
  resetSettings: () => Promise<void>;
  exportSettings: () => Promise<void>;
  importSettings: (jsonData: string) => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const lastToastTimeRef = useRef<number>(0);
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const settingsQueryKey = useMemo(() => ['settings', currentUser?.uid], [currentUser?.uid]);
  
  // Debounced toast function to prevent duplicate toasts
  const showSuccessToast = useMemo(() => {
    const TOAST_ID = 'settings-update-success';
    return (message: string) => {
      const now = Date.now();
      const timeSinceLastToast = now - lastToastTimeRef.current;
      
      // Clear any existing timeout
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
      
      // If a toast was shown recently, debounce it by dismissing and recreating
      if (timeSinceLastToast < 1000) {
        // Dismiss the existing toast with this ID
        toast.dismiss(TOAST_ID);
      }
      
      // Create a new toast with the same ID (this replaces any existing one)
      toastTimeoutRef.current = setTimeout(() => {
        toast.success(message, { 
          id: TOAST_ID,
          duration: 2000,
        });
        lastToastTimeRef.current = Date.now();
      }, timeSinceLastToast < 1000 ? 300 : 0);
    };
  }, []);

  // Fetch settings query
  const {
    data: settings,
    isLoading,
    error,
  } = useQuery({
    queryKey: settingsQueryKey,
    queryFn: async () => {
      try {
        return await settingsService.getSettings(currentUser!.uid);
      } catch (error) {
        // SECURITY: Removed console.error to prevent information leakage
        throw error;
      }
    },
    enabled: !!currentUser?.uid,   // this already prevents the call until we have a UID
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2, // Retry failed requests twice
  });

  // Update settings mutation with optimistic updates
  const updateSettingsMutation = useMutation({
    mutationFn: async (updates: SettingsUpdate) => {
      if (!currentUser?.uid) {
        throw new Error('User not authenticated');
      }
      // Convert SettingsUpdate to the format expected by the service
      const updateData: any = {};
      
      if (updates.profile) {
        updateData.profile = { ...settings?.profile, ...updates.profile };
      }
      if (updates.notifications) {
        updateData.notifications = { ...settings?.notifications, ...updates.notifications };
      }
      if (updates.privacy) {
        updateData.privacy = { ...settings?.privacy, ...updates.privacy };
      }
      if (updates.security) {
        updateData.security = { ...settings?.security, ...updates.security };
      }
      if (updates.payments) {
        updateData.payments = { ...settings?.payments, ...updates.payments };
      }
      if (updates.addresses) {
        updateData.addresses = { ...settings?.addresses, ...updates.addresses };
      }
      if (updates.loyalty) {
        updateData.loyalty = { ...settings?.loyalty, ...updates.loyalty };
      }
      if (updates.preferences) {
        updateData.preferences = { ...settings?.preferences, ...updates.preferences };
      }
      if (updates.metadata) {
        updateData.metadata = { ...settings?.metadata, ...updates.metadata };
      }
      
      await settingsService.updateSettings(currentUser.uid, updateData);
      return settingsService.getSettings(currentUser.uid);
    },
    onMutate: async (updates: SettingsUpdate) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: settingsQueryKey });

      // Snapshot the previous value
      const previousSettings = queryClient.getQueryData(settingsQueryKey);

      // Optimistically update to the new value
      if (settings) {
        const optimisticSettings = {
          ...settings,
          ...updates,
          metadata: {
            ...settings.metadata,
            lastUpdated: new Date(),
          }
        };
        queryClient.setQueryData(settingsQueryKey, optimisticSettings);
      }

      // Return a context object with the snapshotted value
      return { previousSettings };
    },
    onError: (err: Error, updates: SettingsUpdate, context: any) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousSettings) {
        queryClient.setQueryData(settingsQueryKey, context.previousSettings);
      }
      toast.error('Failed to update settings', { id: 'settings-error' });
    },
    onSuccess: (updatedSettings: Settings) => {
      // Update the cache with the new data
      queryClient.setQueryData(settingsQueryKey, updatedSettings);
      showSuccessToast('Settings updated successfully');
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: settingsQueryKey });
    },
  });

  // Reset settings mutation
  const resetSettingsMutation = useMutation({
    mutationFn: async () => {
      if (!currentUser?.uid) {
        throw new Error('User not authenticated');
      }
      await settingsService.resetSettings(currentUser.uid);
      return settingsService.getSettings(currentUser.uid);
    },
    onSuccess: (updatedSettings: Settings) => {
      queryClient.setQueryData(settingsQueryKey, updatedSettings);
      toast.success('Settings reset to defaults');
    },
    onError: () => {
      toast.error('Failed to reset settings');
    },
  });

  // Export settings mutation
  const exportSettingsMutation = useMutation({
    mutationFn: () => {
      if (!currentUser?.uid) {
        throw new Error('User not authenticated');
      }
      return settingsService.exportSettings(currentUser.uid);
    },
    onSuccess: (jsonData: string) => {
      // Create and download file
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `baakas-settings-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Settings exported successfully');
    },
    onError: () => {
      toast.error('Failed to export settings');
    },
  });

  // Import settings mutation
  const importSettingsMutation = useMutation({
    mutationFn: async (jsonData: string) => {
      if (!currentUser?.uid) {
        throw new Error('User not authenticated');
      }
      await settingsService.importSettings(currentUser.uid, jsonData);
      return settingsService.getSettings(currentUser.uid);
    },
    onSuccess: (updatedSettings: Settings) => {
      queryClient.setQueryData(settingsQueryKey, updatedSettings);
      toast.success('Settings imported successfully');
    },
    onError: () => {
      toast.error('Failed to import settings');
    },
  });

  // Subscribe to real-time updates
  useEffect(() => {
    if (currentUser?.uid) {
      const unsubscribe = settingsService.subscribeToSettings(currentUser.uid, (updatedSettings) => {
        queryClient.setQueryData(settingsQueryKey, updatedSettings);
      });

      return () => {
        unsubscribe(); // Call the returned unsubscribe function
      };
    }
  }, [currentUser?.uid, queryClient, settingsQueryKey]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const value: SettingsContextType = {
    settings,
    isLoading,
    error,
    updateSettings: async (updates: SettingsUpdate) => {
      await updateSettingsMutation.mutateAsync(updates);
    },
    updateProfile: async (updates: Partial<Settings['profile']>) => {
      await updateSettingsMutation.mutateAsync({ profile: updates });
    },
    updateNotifications: async (updates: Partial<Settings['notifications']>) => {
      await updateSettingsMutation.mutateAsync({ notifications: updates });
    },
    updatePrivacy: async (updates: Partial<Settings['privacy']>) => {
      await updateSettingsMutation.mutateAsync({ privacy: updates });
    },
    updateSecurity: async (updates: Partial<Settings['security']>) => {
      await updateSettingsMutation.mutateAsync({ security: updates });
    },
    resetSettings: async () => {
      await resetSettingsMutation.mutateAsync();
    },
    exportSettings: async () => {
      await exportSettingsMutation.mutateAsync();
    },
    importSettings: async (jsonData: string) => {
      await importSettingsMutation.mutateAsync(jsonData);
    },
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
